/* product-info の media / gallery 同期を本体から分離する */
(() => {
  window.AuremeProductInfo = window.AuremeProductInfo || {};

  if (window.AuremeProductInfo.MediaSync) {
    return;
  }

  /* key付きDOMリストの順序維持と、新規クローンの差分反映だけを扱う */
  class OrderedDomSync {
    constructor({ prepareClonedItem = undefined } = {}) {
      this.prepareClonedItem = prepareClonedItem;
    }

    sync({ container, currentSelector, incomingItems, getKey, orderedKeys }) {
      if (!container) return [];

      const currentItems = Array.from(container.querySelectorAll(currentSelector));
      const currentMap = this.createItemMap(currentItems, getKey);
      const incomingMap = this.createItemMap(incomingItems, getKey);
      const nextOrder = this.resolveNextOrder({
        currentItems,
        incomingItems,
        incomingMap,
        getKey,
        orderedKeys,
      });

      /* 順序もアイテムも変わっていなければ要素の更新をスキップする */
      const currentOrder = currentItems.map(getKey);
      if (
        currentOrder.length === nextOrder.length &&
        currentOrder.every((key, i) => key === nextOrder[i])
      ) {
        return nextOrder;
      }

      const fragment = document.createDocumentFragment();
      for (const [index, key] of nextOrder.entries()) {
        const currentItem = currentMap.get(key);
        const incomingItem = incomingMap.get(key);
        const item = currentItem || incomingItem?.cloneNode(true);
        if (!item) continue;

        if (!currentItem) {
          this.prepareClonedItem?.(item, index);
        }

        fragment.append(item);
      }
      container.replaceChildren(fragment);

      return nextOrder;
    }

    createItemMap(items, getKey) {
      const itemMap = new Map();
      for (const item of items) {
        const key = getKey(item);
        if (!key) continue;
        itemMap.set(key, item);
      }
      return itemMap;
    }

    resolveNextOrder({ currentItems, incomingItems, incomingMap, getKey, orderedKeys }) {
      const nextOrder = [];
      const seenKeys = new Set();

      const pushKey = (key) => {
        if (!key || seenKeys.has(key) || !incomingMap.has(key)) return;
        nextOrder.push(key);
        seenKeys.add(key);
      };

      if (Array.isArray(orderedKeys)) {
        for (const key of orderedKeys) {
          pushKey(key);
        }
      }

      for (const item of currentItems) {
        pushKey(getKey(item));
      }

      for (const item of incomingItems) {
        pushKey(getKey(item));
      }

      return nextOrder;
    }
  }

  /* ProductInfo の状態を参照しつつ、ギャラリー順序、ナビゲーション、モーダル同期だけを扱う */
  class MediaSync {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.galleryMediaOrder = undefined;
      this.orderedDomSync = new OrderedDomSync({
        prepareClonedItem: this.normalizeGalleryItemLoading.bind(this),
      });
    }

    get sourceSectionId() {
      return this.productInfo.sectionId;
    }

    get currentSectionId() {
      return this.productInfo.dataset.section;
    }

    get productModal() {
      return this.productInfo.productModal;
    }

    updateMedia(
      html,
      { preferredMediaId = undefined, fallbackMediaId = undefined } = {},
      mediaActivationOptions = undefined,
    ) {
      const mediaGallerySource = this.productInfo.querySelector('media-gallery ul');
      const mediaGalleryDestination = html.querySelector(`media-gallery ul`);
      const mediaGalleryElement = this.productInfo.querySelector('media-gallery');

      if (mediaGallerySource && mediaGalleryDestination) {
        const incomingMediaItems = Array.from(
          mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
        );
        const mediaOrder = this.orderedDomSync.sync({
          container: mediaGallerySource,
          currentSelector: 'li[data-media-id]',
          incomingItems: incomingMediaItems,
          getKey: (item) => item.dataset.mediaId,
          orderedKeys: this.getStableGalleryMediaOrder(
            incomingMediaItems.map((item) => item.dataset.mediaId),
          ),
        });

        if (this.productInfo.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);

        /* PDP 専用ナビゲーションも、現在のメインギャラリー順を保ったまま差分だけ同期する */
        this.syncAuremeNavigation({
          html,
          mediaGalleryElement,
          id: 'GalleryDots',
          expectedClassName: 'aureme-product-gallery__dots',
          mediaOrder,
        });
        this.syncAuremeNavigation({
          html,
          mediaGalleryElement,
          id: 'GalleryThumbnails',
          expectedClassName: 'aureme-product-gallery__thumbnails',
          mediaOrder,
        });

        const shouldSkipImmediateGallerySync =
          mediaActivationOptions?.skipImmediateGallerySync ||
          mediaActivationOptions?.viewerScrollBehavior === 'smooth';
        mediaGalleryElement?.refreshNavigationItems?.();
        /*
         * 色選択時のSP横スライドでは、resetPages側の即時同期が先に走ると
         * setActiveMediaのsmoothスクロールを打ち消すため、自動同期だけ止める。
         */
        mediaGalleryElement?.querySelector('[id^="GalleryViewer"]')?.resetPages?.({
          syncToActive: !shouldSkipImmediateGallerySync,
        });
      }

      const nextActiveMediaId = this.resolvePreferredMediaId(mediaGalleryElement, {
        preferredMediaId,
        fallbackMediaId,
      });
      if (!nextActiveMediaId) return;

      /* 選択バリアントの代表画像まで移動するが、ギャラリーの並び順自体は保持する */
      mediaGalleryElement?.setActiveMedia?.(
        `${this.currentSectionId}-${nextActiveMediaId}`,
        mediaActivationOptions,
      );

      /* メディアモーダルを更新 */
      const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
      const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
      if (modalContent && newModalContent) {
        this.orderedDomSync.sync({
          container: modalContent,
          currentSelector: ':scope > [data-media-id]',
          incomingItems: Array.from(newModalContent.querySelectorAll(':scope > [data-media-id]')),
          getKey: (item) => String(item.dataset.mediaId || ''),
          orderedKeys: this.getModalMediaOrder(),
        });
      }
    }

    /*
     * 初回表示時のギャラリー順を保持し、再表示メディアも元の位置へ戻せるようにする。
     * サーバーが返すメディア順はバリアントごとに変わりうるため、DOM 順を毎回信用すると
     * 色替え時にギャラリーの並びが入れ替わって見える。初回順を固定の正として使い回す。
     */
    getStableGalleryMediaOrder(incomingKeys) {
      const normalizedIncomingKeys = incomingKeys.filter(Boolean);
      const incomingKeySet = new Set(normalizedIncomingKeys);

      if (!Array.isArray(this.galleryMediaOrder)) {
        this.galleryMediaOrder = Array.from(
          this.productInfo.querySelectorAll('media-gallery ul li[data-media-id]'),
        )
          .map((item) => item.dataset.mediaId)
          .filter(Boolean);
      }

      const knownKeySet = new Set(this.galleryMediaOrder);
      for (const key of normalizedIncomingKeys) {
        if (knownKeySet.has(key)) continue;
        this.galleryMediaOrder.push(key);
        knownKeySet.add(key);
      }

      return this.galleryMediaOrder.filter((key) => incomingKeySet.has(key));
    }

    /* メインギャラリーで保持した順序を、モーダル用の生メディアID配列へ変換する */
    getModalMediaOrder() {
      if (!Array.isArray(this.galleryMediaOrder)) return [];

      const mediaIdPrefix = `${this.currentSectionId}-`;
      return this.galleryMediaOrder
        .map((key) =>
          typeof key === 'string' && key.startsWith(mediaIdPrefix)
            ? key.slice(mediaIdPrefix.length)
            : key,
        )
        .filter(Boolean);
    }

    normalizeGalleryItemLoading(item, index) {
      if (!(item instanceof HTMLElement)) return;
      if (index === 0) return;

      for (const image of item.querySelectorAll('img')) {
        image.setAttribute('loading', 'lazy');
      }
    }

    syncAuremeNavigation({ html, mediaGalleryElement, id, expectedClassName, mediaOrder }) {
      const source = html.querySelector(`media-gallery #${id}-${this.sourceSectionId}`);
      const destination = mediaGalleryElement?.querySelector(`#${id}-${this.currentSectionId}`);

      if (!source || !destination) return;
      if (
        !source.classList.contains(expectedClassName) ||
        !destination.classList.contains(expectedClassName)
      ) {
        return;
      }

      const destinationList = destination.querySelector('ul');
      const sourceItems = Array.from(source.querySelectorAll('[data-target]'));
      if (!destinationList) return;

      destination.className = source.className;

      const orderedTargets = Array.isArray(mediaOrder) ? mediaOrder : undefined;

      this.orderedDomSync.sync({
        container: destinationList,
        currentSelector: '[data-target]',
        incomingItems: sourceItems,
        getKey: (item) => item.dataset.target,
        orderedKeys: orderedTargets,
      });

      /* カウンター計算とアクティブ状態更新のため、位置番号だけは現在順に振り直す */
      for (const [index, item] of Array.from(
        destinationList.querySelectorAll('[data-target]'),
      ).entries()) {
        const mediaPosition = String(index + 1);
        item.dataset.mediaPosition = mediaPosition;
        const button = item.querySelector('button');
        button?.removeAttribute('aria-current');

        if (id === 'GalleryDots' && button && window.accessibilityStrings?.loadImage) {
          button.setAttribute(
            'aria-label',
            window.accessibilityStrings.loadImage.replace('[index]', mediaPosition),
          );
        }
      }
    }

    resolvePreferredMediaId(
      mediaGalleryElement,
      { preferredMediaId = undefined, fallbackMediaId = undefined } = {},
    ) {
      const candidateIds = [preferredMediaId, fallbackMediaId]
        .map((mediaId) => String(mediaId || '').trim())
        .filter(Boolean);
      if (!candidateIds.length) return undefined;
      if (!mediaGalleryElement) return candidateIds[0];

      const sectionPrefix = `${this.currentSectionId}-`;
      const galleryMediaIds = new Set(
        Array.from(mediaGalleryElement.querySelectorAll('[data-media-id]')).map(
          (item) => item.dataset.mediaId,
        ),
      );

      for (const mediaId of candidateIds) {
        if (galleryMediaIds.has(`${sectionPrefix}${mediaId}`)) {
          return mediaId;
        }
      }

      return candidateIds[0];
    }

    syncSwappedProductMedia({
      selector,
      preferredMediaId = undefined,
      fallbackMediaId = undefined,
      mediaActivationOptions = undefined,
    } = {}) {
      if (!selector) return;

      requestAnimationFrame(() => {
        const currentProductInfo = document.querySelector(selector);
        const mediaGalleryElement = currentProductInfo?.querySelector('media-gallery');
        if (!currentProductInfo || !mediaGalleryElement) return;
        const selectedColorMediaId = currentProductInfo.getSelectedColorMediaId?.();

        const nextActiveMediaId = currentProductInfo.resolvePreferredMediaId(mediaGalleryElement, {
          preferredMediaId: selectedColorMediaId || preferredMediaId,
          fallbackMediaId,
        });
        if (!nextActiveMediaId) return;

        mediaGalleryElement.setActiveMedia?.(
          `${currentProductInfo.dataset.section}-${nextActiveMediaId}`,
          mediaActivationOptions,
        );
      });
    }
  }

  window.AuremeProductInfo.MediaSync = MediaSync;
})();
